import * as React from "react";
import { useTranslation } from "react-i18next";
import memoize from "lodash/memoize";
import { useQuery } from "@tanstack/react-query";
import useFieldTypes from "views/logic/fieldtypes/useFieldTypes";
import { Col, ControlLabel, FormGroup, HelpBlock, Row } from "components/bootstrap";
import { Select, Spinner } from "components/common";
import { naturalSortIgnoreCase } from "util/SortUtils";
import usePermissions from "hooks/usePermissions";
import { ALL_MESSAGES_TIMERANGE } from "views/Constants";
import { fetchAllLookupTables } from "components/lookup-tables/hooks/api/lookupTablesAPI";
const LOOKUP_PERMISSIONS = ["lookuptables:read"];
const LookupTableFields = ({
  onTableNameChange,
  onKeyChange,
  selectedTableName,
  selectedKeyName,
  nameValidation,
  keyValidation,
  lookupTableNameLabel = "",
  lookupTableKeyLabel = "",
  customKeyField = null
}) => {
  const { t } = useTranslation("lookup_tables");
  const { data: allFieldTypes } = useFieldTypes([], ALL_MESSAGES_TIMERANGE);
  const { isPermitted } = usePermissions();
  const hasPermission = isPermitted(LOOKUP_PERMISSIONS);
  const { data: tables } = useQuery({
    queryKey: ["lookup_tables", "all"],
    queryFn: () => fetchAllLookupTables(),
    enabled: hasPermission
  });
  if (!hasPermission) {
    return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 6, lg: 5 }, /* @__PURE__ */ React.createElement("p", null, t("table_fields.none_found"))));
  }
  const formatMessageFields = memoize(
    (fieldTypes) => fieldTypes.sort((ftA, ftB) => naturalSortIgnoreCase(ftA.name, ftB.name)).map((fieldType) => ({
      label: `${fieldType.name} \u2013 ${fieldType.value.type.type}`,
      value: fieldType.name
    })),
    (fieldTypes) => fieldTypes.map((ft) => ft.name).join("-")
  );
  const formatLookupTables = (items) => items.sort((lt1, lt2) => naturalSortIgnoreCase(lt1.title, lt2.title)).map((table) => ({ label: table.title, value: table.name }));
  const isLoading = !allFieldTypes || !tables;
  if (isLoading) {
    return /* @__PURE__ */ React.createElement(Spinner, { text: t("table_fields.loading") });
  }
  return /* @__PURE__ */ React.createElement(Row, { className: "row-sm" }, /* @__PURE__ */ React.createElement(Col, { md: 6 }, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "lookup-provider-table", validationState: nameValidation ? "error" : null }, /* @__PURE__ */ React.createElement(ControlLabel, null, lookupTableNameLabel || t("table_fields.table_label")), /* @__PURE__ */ React.createElement(
    Select,
    {
      name: "event-field-table-name",
      placeholder: t("table_fields.table_label"),
      onChange: onTableNameChange,
      options: formatLookupTables(tables),
      value: selectedTableName,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, nameValidation || t("table_fields.table_help")))), customKeyField ? /* @__PURE__ */ React.createElement(Col, { md: 6 }, customKeyField) : /* @__PURE__ */ React.createElement(Col, { md: 6 }, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "lookup-provider-table", validationState: keyValidation ? "error" : null }, /* @__PURE__ */ React.createElement(ControlLabel, null, lookupTableKeyLabel || t("table_fields.key_label")), /* @__PURE__ */ React.createElement(
    Select,
    {
      name: "lookup-provider-key",
      placeholder: t("table_fields.key_placeholder"),
      onChange: onKeyChange,
      options: formatMessageFields(allFieldTypes),
      value: selectedKeyName,
      allowCreate: true,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, keyValidation || t("table_fields.key_help")))));
};
export default LookupTableFields;
